import { useState, useMemo, useCallback } from 'react';

/**
 * Custom hook for client-side table sorting
 * 
 * @param {Array} data - The original data array (will not be mutated)
 * @param {Object} options - Configuration options
 * @param {Object} options.columnTypes - Map of column keys to their types: 'text' | 'number'
 * @param {string} options.defaultSortColumn - Initial column to sort by (optional)
 * @param {string} options.defaultSortDirection - Initial direction: 'asc' | 'desc' (optional)
 * @param {boolean} options.resetOnDataChange - Reset sort state when data changes (default: true)
 * 
 * @returns {Object} - { sortedData, sortColumn, sortDirection, handleSort, resetSort }
 */
export function useSortableData(data, options = {}) {
  const {
    columnTypes = {},
    defaultSortColumn = null,
    defaultSortDirection = null,
    resetOnDataChange = true
  } = options;

  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  // Handle column header click
  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: determine initial direction based on type
      const columnType = columnTypes[column] || 'text';
      const initialDirection = columnType === 'number' ? 'desc' : 'asc';
      setSortColumn(column);
      setSortDirection(initialDirection);
    }
  }, [sortColumn, columnTypes]);

  // Reset to default/unsorted state
  const resetSort = useCallback(() => {
    setSortColumn(defaultSortColumn);
    setSortDirection(defaultSortDirection);
  }, [defaultSortColumn, defaultSortDirection]);

  // Compute sorted data
  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return data || [];
    }

    // If no sort column is selected, return original order
    if (!sortColumn || !sortDirection) {
      return [...data];
    }

    const columnType = columnTypes[sortColumn] || 'text';
    
    return [...data].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Handle null/undefined values - push them to the end
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let comparison = 0;

      if (columnType === 'number') {
        // Numeric comparison
        const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue) || 0;
        const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue) || 0;
        comparison = aNum - bNum;
      } else {
        // Text comparison (case-insensitive)
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        comparison = aStr.localeCompare(bStr, 'pt-BR');
      }

      // Apply direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortColumn, sortDirection, columnTypes]);

  return {
    sortedData,
    sortColumn,
    sortDirection,
    handleSort,
    resetSort
  };
}

export default useSortableData;
